import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button,
  ButtonLink,
  Column,
  DescriptionList,
  FormBox,
  InputPhoneNumber,
  PageActions,
  Row,
} from "@ficatranquilo/contabilidade.com-ui";
import useService from "@hooks/useService";
import { formatPhone } from "@helpers";
import { DocumentNumber, usePageHeader } from "@components/ow";
import { FormPage, FormPageSkeleton, FullWidth, focusFirstError } from "../FormPage";

// Perfil do próprio usuário (modelo Formulário do DS OneWork), do drawer "Perfil do
// usuário". O cargo é do vínculo com a empresa ativa; o resto, da conta. Só o WhatsApp
// muda por aqui.

const BREADCRUMB = [{ page: "Meu perfil", route: "/profile" }];

const PHONE_ID = "profile-phone";

const onlyDigits = (value) => String(value || "").replace(/\D/g, "");

/** Vazio limpa o número; começado, precisa do DDD e dos 9 dígitos. */
export function phoneError(phone) {
  const digits = onlyDigits(phone);
  return digits.length > 0 && digits.length < 11 ? "Número de WhatsApp inválido." : undefined;
}

export default function Profile() {
  const userService = useService("users");
  const { id } = JSON.parse(localStorage.getItem("user_ow") || "{}");
  const [user, setUser] = useState(null);
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  // O erro aparece no campo depois da primeira tentativa e se atualiza a cada mudança
  const [submitted, setSubmitted] = useState(false);

  usePageHeader({ title: "Meu perfil", breadcrumb: BREADCRUMB });

  useEffect(() => {
    loadUser().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadUser() {
    try {
      const { data } = await userService.get(id);
      setUser(data);
      setPhone(formatPhone(data?.phone));
    } catch (err) {
      console.error(err);
      toast.error("Não foi possível carregar seu perfil");
    }
  }

  const savedPhone = formatPhone(user?.phone);
  const dirty = onlyDigits(phone) !== onlyDigits(savedPhone);
  const fieldError = submitted ? phoneError(phone) : undefined;

  function discard() {
    setPhone(savedPhone);
    setSubmitted(false);
  }

  async function handleSubmit() {
    setSubmitted(true);
    if (phoneError(phone)) {
      focusFirstError([PHONE_ID]);
      return;
    }

    setSaving(true);
    const digits = onlyDigits(phone);
    const result = await userService.update({ phone: digits }, id);
    setSaving(false);
    // O update da base engole o erro e devolve vazio
    if (!result || result.error) {
      toast.error("Erro ao atualizar o WhatsApp");
      return;
    }
    toast.success("WhatsApp atualizado");
    setUser((prev) => ({ ...prev, phone: digits }));
    setSubmitted(false);
  }

  if (loading) return <FormPageSkeleton sections={[150, 130]} />;

  return (
    <FormPage
      actions={
        <PageActions>
          {dirty ? <ButtonLink $text="Descartar alterações" $action={discard} /> : null}
          <Button
            $text="Salvar WhatsApp"
            $variant="primary"
            $action={handleSubmit}
            $isLoading={saving}
            $isDisabled={!dirty || !user}
          />
        </PageActions>
      }
    >
      <FormBox title="Seus dados">
        <FullWidth>
          <DescriptionList
            $columns={2}
            $items={[
              { label: "Nome", value: user?.name },
              { label: "E-mail", value: user?.email },
              { label: "Cargo", value: user?.job },
              { label: "Documento", value: user?.document ? <DocumentNumber value={user.document} /> : null },
            ]}
          />
        </FullWidth>
      </FormBox>

      <FormBox title="Contato">
        <Row $gap={20}>
          <Column $columns={4}>
            <InputPhoneNumber
              $idForm={PHONE_ID}
              $label="WhatsApp"
              $variant="default"
              $errors={fieldError}
              placeholder="(11) 99999-9999"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </Column>
        </Row>
      </FormBox>
    </FormPage>
  );
}
